import React, { useEffect, useState } from "react";
import { Minus, Plus, Clock, ChevronDown } from "lucide-react";
import { get } from "./techData";
import { Card } from "./ui";

export const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const hourly = (n) => /hourly/i.test(n);
const pSum = (ps) => (ps || []).reduce((a, p) => a + (Number(p.price) || 0) * p.qty, 0);
const lSum = (ls) => (ls || []).reduce((a, x) => a + x.hours * (x.rate || 0), 0);
const added = (v, f) => (v.added || []).reduce((a, x) => a + f(x), 0);
export const pendingTotal = (v) => pSum(v.parts) + lSum(v.labour);
export const partsTotal = (v) => pSum(v.parts) + added(v, (x) => pSum(x.parts));
export const labourTotal = (v) => lSum(v.labour) + added(v, (x) => lSum(x.labour));
export const visitTotal = (v) => partsTotal(v) + labourTotal(v);

export function RunningTotal({ v }) {
  if (!visitTotal(v)) return null;
  return (
    <Card className="space-y-1 bg-[#0f2f52] text-white" data-testid="tech-running-total">
      <div className="flex justify-between text-[16px]"><span>Labour</span><span>{money(labourTotal(v))}</span></div>
      <div className="flex justify-between text-[16px]"><span>Parts</span><span>{money(partsTotal(v))}</span></div>
      <div className="flex justify-between text-[21px] font-black border-t border-white/30 pt-1"><span>Today's total</span><span data-testid="tech-running-total-value">{money(visitTotal(v))}</span></div>
    </Card>
  );
}

// quick buttons: each label IS the exact Labour Charge name in Table_LabourCharges; rate always read live
const QUICK = ["Local Service Callout Fee", "Bay Service Callout Fee", "Regular Pool Service", "Regular Spa Service", "Regular Runback", "Bay Runback", "Hourly Rate Pools", "Hourly Rate Spas"];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

// labour lines come from the office Labour Charges rate card; hourly ones step by ¼ h
export default function LabourCard({ v, setV, live }) {
  const [rates, setRates] = useState([]), [more, setMore] = useState(false);
  useEffect(() => { get("/labour-charges").then(setRates).catch(() => {}); }, []);
  const change = (r, d) => (live ? live.bump(r, d) : setV((cur) => {
    const step = hourly(r.name) ? 0.25 : 1, has = cur.labour.find((x) => x.description === r.name);
    const labour = has ? cur.labour.map((x) => (x.description === r.name ? { ...x, hours: Math.max(0, Math.round((x.hours + d * step) * 4) / 4) } : x)).filter((x) => x.hours > 0)
      : d > 0 ? [...cur.labour, { description: r.name, hours: step, rate: r.rate }] : cur.labour;
    return { labour };
  }));
  const qty = (n) => (live ? live.qty(n) : v.labour.find((x) => x.description === n)?.hours || 0);
  const total = live ? live.total : lSum(v.labour);
  const Row = ({ r }) => {
    const n = qty(r.name);
    return (
      <div className={`flex items-center gap-2 rounded-xl p-2 border-2 ${n ? "border-amber-500 bg-amber-50" : "border-slate-100"}`} data-testid={`tech-labour-${r.name}`}>
        <div className="flex-1 min-w-0"><div className="text-[16px] font-black text-slate-800 leading-tight">{r.name}</div><div className="text-[14px] text-slate-500">{money(r.rate)}{hourly(r.name) ? " / hour" : ""}</div></div>
        {n > 0 && <button onClick={() => change(r, -1)} data-testid={`tech-labour-minus-${r.name}`} className="h-12 w-12 rounded-xl bg-white flex items-center justify-center"><Minus className="h-6 w-6" /></button>}
        {n > 0 && <span className="w-14 text-center text-[20px] font-black text-[#0f2f52]">{n}{hourly(r.name) ? "h" : ""}</span>}
        <button onClick={() => change(r, 1)} data-testid={`tech-labour-plus-${r.name}`} className="h-12 w-12 rounded-xl bg-[#0f2f52] text-white flex items-center justify-center"><Plus className="h-6 w-6" /></button>
      </div>
    );
  };
  const Quick = (cap) => {
    const r = rates.find((x) => x.name.trim() === cap), n = r ? qty(r.name) : 0;
    return (
      <button key={cap} disabled={!r} onClick={() => r && change(r, 1)} data-testid={`tech-labour-quick-${slug(cap)}`}
        className={`relative min-h-[64px] rounded-xl border-2 px-2 py-1.5 text-center leading-tight transition-transform active:scale-95 ${r ? "bg-[#0f2f52]" : "bg-slate-300"} ${n ? "border-amber-400 ring-2 ring-amber-400" : "border-black"}`}>
        <div className="text-[15px] font-black text-[#ffe600]">{cap}</div>
        <div className="text-[12px] font-bold text-white/80">{r ? `${money(r.rate)}${hourly(r.name) ? " / hr" : ""}` : "Not in Labour Charges"}</div>
        {n > 0 && <span className="absolute -top-2 -right-2 h-7 min-w-[28px] px-1 rounded-full bg-amber-500 text-white text-[14px] font-black flex items-center justify-center">{n}{hourly(r.name) ? "h" : ""}</span>}
      </button>
    );
  };
  return (
    <Card className="space-y-2" data-testid="tech-labour">
      <div className="text-[15px] font-black text-slate-500 flex items-center gap-1"><Clock className="h-4 w-4" />Labour</div>
      <div className="grid grid-cols-2 gap-2">{QUICK.map(Quick)}</div>
      <button onClick={() => setMore(!more)} data-testid="tech-labour-more" className="w-full min-h-[56px] rounded-xl border-2 border-black bg-[#0f2f52] text-[18px] font-black tracking-wide text-[#ffe600] inline-flex items-center justify-center gap-2">Other Labour Charge<ChevronDown className={`h-5 w-5 transition-transform ${more ? "rotate-180" : ""}`} /></button>
      {more && <div className="space-y-2" data-testid="tech-labour-all">{[...rates].sort((a, b) => a.name.localeCompare(b.name)).map((r) => <Row key={r.name} r={r} />)}</div>}
      {!more && !live && v.labour.length > 0 && <div className="space-y-2" data-testid="tech-labour-picked">{v.labour.map((x) => <Row key={x.description} r={{ name: x.description, rate: x.rate }} />)}</div>}
      {total > 0 && <div className="flex justify-between text-[18px] font-black text-[#0f2f52] pt-1" data-testid="tech-labour-total"><span>Labour total</span><span>{money(total)}</span></div>}
    </Card>
  );
}
